import { MutationButton } from "@Light/components/page/MutationButton";
import { PageBody } from "@Light/components/page/PageBody";
import { ErrorMessage, Field } from "@Light/components/system/Fieldset";
import { Input, Select } from "@Light/components/system/Input";
import type { RequirementsPatch } from "@Light/services/lightTypes";
import { US_STATES } from "@Light/utils/address";
import { useForm } from "react-hook-form";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { FieldLabel } from "../FieldLabel";
import { useEnrollment } from "../flow/context";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { EnrollRoute } from "../routes";

type ManualServiceAddressForm = {
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postal_code: string;
};

export const ManualServiceAddress = () => {
  const { requirements, next, back, nextMutation } = useEnrollment();
  const stepper = useEnrollmentStepper(EnrollRoute.ManualServiceAddress);
  const address = requirements.service_address?.value;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ManualServiceAddressForm>();

  const onSubmit = handleSubmit(async (data) => {
    const patch: RequirementsPatch = {
      service_address: {
        address_1: data.address_1.trim(),
        address_2: data.address_2.trim() || null,
        city: data.city.trim(),
        state: data.state,
        postal_code: data.postal_code.trim(),
      },
    };
    await next(patch);
  });

  return (
    <form onSubmit={onSubmit}>
      <PageBody
        stepper={stepper}
        title="What’s your service address?"
        onBack={back}
        footer={
          <div className="flex flex-col gap-3">
            <EnrollHelpButtonFooter>
              <MutationButton
                size="lg"
                mutation={nextMutation}
                mutateButtonText="Continue"
                errorMessage="Failed saving your service address. Please try again later"
                data-testid="manual-service-address-submit"
              />
            </EnrollHelpButtonFooter>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Field className="flex flex-col gap-1">
            <FieldLabel>Address</FieldLabel>
            <Input
              type="text"
              autoComplete="address-line1"
              placeholder="123 Main St"
              isError={!!errors.address_1}
              data-record="true"
              data-testid="manual-service-address-address-1"
              {...register("address_1", {
                required: true,
                value: address?.address_1 ?? "",
              })}
            />
            <ErrorMessage error={errors.address_1?.message} />
          </Field>

          <Field className="flex flex-col gap-1">
            <FieldLabel>Apt, suite, etc. (optional)</FieldLabel>
            <Input
              type="text"
              autoComplete="address-line2"
              placeholder="Apt 4B"
              data-record="true"
              data-testid="manual-service-address-address-2"
              {...register("address_2", { value: address?.address_2 ?? "" })}
            />
          </Field>

          <Field className="flex flex-col gap-1">
            <FieldLabel>City</FieldLabel>
            <Input
              type="text"
              autoComplete="address-level2"
              placeholder="Austin"
              isError={!!errors.city}
              data-record="true"
              data-testid="manual-service-address-city"
              {...register("city", {
                required: true,
                value: address?.city ?? "",
              })}
            />
            <ErrorMessage error={errors.city?.message} />
          </Field>

          <div className="flex flex-row gap-3">
            <Field className="flex flex-col gap-1 flex-1 min-w-0">
              <FieldLabel>State</FieldLabel>
              <Select
                autoComplete="address-level1"
                defaultValue={address?.state ?? ""}
                isError={!!errors.state}
                data-record="true"
                data-testid="manual-service-address-state"
                {...register("state", { required: true })}
              >
                <option value="" disabled hidden>
                  Select
                </option>
                {US_STATES.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field className="flex flex-col gap-1 flex-none w-32">
              <FieldLabel>Zip code</FieldLabel>
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="postal-code"
                placeholder="78701"
                maxLength={5}
                isError={!!errors.postal_code}
                data-record="true"
                data-testid="manual-service-address-postal-code"
                {...register("postal_code", {
                  required: true,
                  pattern: {
                    value: /^\d{5}$/,
                    message: "Enter a 5-digit zip code",
                  },
                  value: address?.postal_code?.slice(0, 5) ?? "",
                })}
              />
            </Field>
          </div>
          <ErrorMessage
            error={errors.state?.message ?? errors.postal_code?.message}
          />
        </div>
      </PageBody>
    </form>
  );
};
